import cachedWeb3Utils from '../utils/cachedWeb3Utils.js';

// Helper function to convert wei to ether
function weiToEther(weiAmount) {
    return weiAmount / Math.pow(10, 18);
}

// Helper functions for Uniswap V3 calculations
function tickToSqrtPriceX96(tick) {
    // More precise calculation using Math.pow and proper BigInt conversion
    const sqrtPrice = Math.pow(1.0001, tick / 2);
    const sqrtPriceX96 = sqrtPrice * Math.pow(2, 96);
    return BigInt(Math.floor(sqrtPriceX96));
}

function sqrtPriceX96ToPrice(sqrtPriceX96) {
    const sqrtPrice = Number(sqrtPriceX96) / Math.pow(2, 96);
    return sqrtPrice * sqrtPrice;
}

function calculateDelta0(sqrtPriceA, sqrtPriceB, liquidity) {
    // Calculate delta token0 for a price range
    // delta0 = liquidity * (1/sqrtPriceB - 1/sqrtPriceA)
    const sqrtPriceANum = Number(sqrtPriceA) / Math.pow(2, 96);
    const sqrtPriceBNum = Number(sqrtPriceB) / Math.pow(2, 96);
    const liquidityNum = Number(liquidity);
    
    if (sqrtPriceANum === 0 || sqrtPriceBNum === 0) return 0;
    
    return liquidityNum * (1 / sqrtPriceBNum - 1 / sqrtPriceANum);
}

function calculateDelta1(sqrtPriceA, sqrtPriceB, liquidity) {
    // Calculate delta token1 for a price range
    // delta1 = liquidity * (sqrtPriceB - sqrtPriceA)
    const sqrtPriceANum = Number(sqrtPriceA) / Math.pow(2, 96);
    const sqrtPriceBNum = Number(sqrtPriceB) / Math.pow(2, 96);
    const liquidityNum = Number(liquidity);
    
    return liquidityNum * (sqrtPriceBNum - sqrtPriceANum);
}

/**
 * 计算从起始价格到目标价格需要消耗的 token 数量
 * @param {BigInt} startSqrtPriceX96 - 起始的 sqrtPriceX96
 * @param {BigInt} targetSqrtPriceX96 - 目标的 sqrtPriceX96  
 * @param {BigInt} liquidity - 当前流动性
 * @param {boolean} isToken0 - true 表示计算 token0 消耗，false 表示 token1 消耗
 * @returns {Object} 包含消耗的 token 数量和价格信息
 */
function calculateTokenConsumptionBetweenPrices(startSqrtPriceX96, targetSqrtPriceX96, liquidity, isToken0 = true) {
    let tokenConsumption;
    if (isToken0) {
        tokenConsumption = Math.abs(calculateDelta0(startSqrtPriceX96, targetSqrtPriceX96, liquidity));
    } else {
        tokenConsumption = Math.abs(calculateDelta1(startSqrtPriceX96, targetSqrtPriceX96, liquidity));
    }
    
    const startPrice = sqrtPriceX96ToPrice(startSqrtPriceX96);
    const targetPrice = sqrtPriceX96ToPrice(targetSqrtPriceX96);
    
    return {
        startPrice: startPrice,
        targetPrice: targetPrice,
        tokenConsumptionEther: weiToEther(tokenConsumption),
        tokenType: isToken0 ? 'token0' : 'token1',
        priceChange: (targetPrice - startPrice) / startPrice * 100
    };
}

/**
 * 计算多个tick区间的流动性代币消耗（支持上涨和下跌两个方向）
 * 这个函数会处理价格变化过程中tick跳变和流动性变化
 * @param {BigInt} startSqrtPriceX96 - 起始价格
 * @param {number} startTick - 起始tick
 * @param {BigInt} initialLiquidity - 初始流动性
 * @param {number} tickSpacing - tick间距
 * @param {Object} tickLUT - tick查找表，以tick ID为键
 * @param {number} targetTick - 目标tick
 * @param {boolean} isToken0 - 计算token0还是token1的消耗
 * @returns {Object} 包含详细的计算结果
 */
function calculateMultiTickConsumption(startSqrtPriceX96, startTick, initialLiquidity, tickSpacing, tickLUT, targetTick, isToken0 = false) {
    const results = [];
    let currentLiquidity = BigInt(initialLiquidity);
    let currentSqrtPriceX96 = BigInt(startSqrtPriceX96);
    let currentTick = startTick;
    let totalTokenConsumption = 0;

    // 判断方向：true为向上（价格上涨），false为向下（价格下跌）
    const isUpward = startTick < targetTick;
    
    // 计算需要处理的tick数量
    const tickCount = Math.floor(Math.abs(startTick - targetTick) / tickSpacing) + 1;
    
    
    // 统一的循环处理所有段落
    for (let i = 0; i < tickCount; i++) {
        let segmentTargetTick, targetSqrtPriceX96;
        
        // 在计算token消耗之前，先处理流动性变化（除了第一段）
        if (i > 0) {
            // 当跨越tick边界时，使用前一个tick的liquidityNet来调整流动性
            const previousTick = isUpward ? 
                startTick + (tickSpacing * (i)) : 
                startTick - (tickSpacing * (i - 1));
            const tickData = tickLUT[previousTick];
            
            if (tickData && tickData.liquidityNet) {
                // 价格变化时的流动性调整
                if (isUpward) {
                    // 价格上涨时：当前流动性 + liquidityNet
                    currentLiquidity = currentLiquidity + BigInt(tickData.liquidityNet);
                } else {
                    // 价格下跌时：当前流动性 - liquidityNet
                    currentLiquidity = currentLiquidity - BigInt(tickData.liquidityNet);
                }
            } else {
            }

            // 如果流动性为0或负数，停止计算
            if (currentLiquidity <= 0n) {
                console.log(`Liquidity exhausted after crossing tick ${previousTick}, stopping calculation.`);
                break;
            }
        }
        
        if (i === 0) {
            // 第一段：从当前价格到当前tick的边界
            segmentTargetTick = currentTick;
            const currentTickBounds = calculateTickPriceBounds(currentTick, tickSpacing);
            
            if (isUpward) {
                // 向上：从当前价格涨到当前tick的upper bound
                targetSqrtPriceX96 = BigInt(currentTickBounds.bounds.upper.sqrtPriceX96);
            } else {
                // 向下：从当前价格跌到当前tick的lower bound
                targetSqrtPriceX96 = BigInt(currentTickBounds.bounds.lower.sqrtPriceX96);
            }
        } else {
            // 后续段落：完整的tick区间
            if (isUpward) {
                segmentTargetTick = startTick + (tickSpacing * i);
                // 确保不超过目标tick
                if (segmentTargetTick > targetTick) {
                    segmentTargetTick = targetTick;
                }
                // 计算目标tick区间的价格边界（向上用upper bound）
                const targetTickBounds = calculateTickPriceBounds(segmentTargetTick, tickSpacing);
                targetSqrtPriceX96 = BigInt(targetTickBounds.bounds.upper.sqrtPriceX96);
            } else {
                segmentTargetTick = startTick - (tickSpacing * i);
                // 确保不超过目标tick
                if (segmentTargetTick < targetTick) {
                    segmentTargetTick = targetTick;
                }
                // 计算目标tick区间的价格边界（向下用lower bound）
                const targetTickBounds = calculateTickPriceBounds(segmentTargetTick, tickSpacing);
                targetSqrtPriceX96 = BigInt(targetTickBounds.bounds.lower.sqrtPriceX96);
            }
        }
        
        // 计算token消耗
        const segmentCalc = calculateTokenConsumptionBetweenPrices(
            currentSqrtPriceX96, 
            targetSqrtPriceX96, 
            currentLiquidity, 
            isToken0
        );
        
        totalTokenConsumption += segmentCalc.tokenConsumptionEther;
        
        results.push({
            segment: i + 1,
            startPrice: segmentCalc.startPrice,
            endPrice: segmentCalc.targetPrice,
            startTick: segmentTargetTick,
            liquidity: currentLiquidity.toString(),
            tokenConsumption: segmentCalc.tokenConsumptionEther,
            priceChange: segmentCalc.priceChange,
            direction: isUpward ? 'UPWARD' : 'DOWNWARD',
            isToken0: isToken0,
        });

        // 更新当前价格到新的目标价格
        currentSqrtPriceX96 = targetSqrtPriceX96;
        
        // 如果已经到达目标tick，停止循环
        if (segmentTargetTick === targetTick) {
            break;
        }
    }

    return {
        segments: results,
        totalTokenConsumption: totalTokenConsumption,
        tokenType: isToken0 ? 'token0' : 'token1',
        direction: isUpward ? 'UPWARD' : 'DOWNWARD',
        startPrice: sqrtPriceX96ToPrice(startSqrtPriceX96),
        finalPrice: results[results.length - 1]?.endPrice || sqrtPriceX96ToPrice(startSqrtPriceX96),
        totalPriceChange: results.length > 0 ? 
            (results[results.length - 1].endPrice - sqrtPriceX96ToPrice(startSqrtPriceX96)) / sqrtPriceX96ToPrice(startSqrtPriceX96) * 100 : 0
    };
}

// Helper function to convert price to tick
function priceToTick(price) {
    // price = (1.0001)^tick, so tick = log(price) / log(1.0001)
    return Math.floor(Math.log(price) / Math.log(1.0001));
}

/**
 * 重构的V3池数据获取和计算函数
 * @param {Contract} contract - V3池合约实例
 * @param {number} priceDelta - 价格变化范围（百分比，如5表示5%）
 * @returns {Object} 包含token消耗和流动性分段信息
 */
async function fetchV3PoolData(contract, priceDelta) {
    const [slot0, tickSpacing] = await Promise.all([
        contract.methods.slot0().call(),
        cachedWeb3Utils.getPoolTickSpacing(contract.options.address)
    ]);

    const currentSqrtPriceX96 = slot0.sqrtPriceX96;
    const currentPrice = sqrtPriceX96ToPrice(currentSqrtPriceX96);
    
    // 根据priceDelta计算目标价格
    const priceChangeRatio = priceDelta / 100; // 转换为小数
    const lowerTargetPrice = currentPrice * (1 - priceChangeRatio);
    const upperTargetPrice = currentPrice * (1 + priceChangeRatio);
    
    // 将目标价格转换为tick
    const lowerTargetTick = priceToTick(lowerTargetPrice);
    const upperTargetTick = priceToTick(upperTargetPrice);
    
    // 确保tick对齐到tickSpacing
    const tickSpacingInt = parseInt(tickSpacing);
    const startTick = Math.floor(lowerTargetTick / tickSpacingInt) * tickSpacingInt;
    const endTick = Math.ceil(upperTargetTick / tickSpacingInt) * tickSpacingInt;

    console.log(`Target tick range: ${lowerTargetTick} - ${upperTargetTick}`);

    // 获取tick范围内的数据
    const promises = [];
    for (let tick = startTick; tick <= endTick; tick += tickSpacingInt) {
        promises.push(contract.methods.ticks(tick).call());
    }
    
    promises.push(contract.methods.liquidity().call());
    promises.push(contract.methods.slot0().call());

    const results = await Promise.all(promises);
    
    const ticksData = results.slice(0, -2);
    const liquidity = results[results.length - 2];
    const finalSlot0 = results[results.length - 1];

    // 创建tick查找表（LUT），以tick ID为键
    const tickLUT = {};
    let tickIndex = 0;
    for (let tick = startTick; tick <= endTick; tick += tickSpacingInt) {
        if (tickIndex < ticksData.length) {
            tickLUT[tick] = ticksData[tickIndex];
            tickIndex++;
        }
    }

    // 使用最新的 finalSlot0 数据重新计算
    const finalCurrentTick = parseInt(finalSlot0.tick);
    const finalSqrtPriceX96 = finalSlot0.sqrtPriceX96;

    console.log('=== Pool Data ===');
    console.log('Final current tick:', finalCurrentTick);
    console.log('Tick spacing:', tickSpacing.toString());
    console.log('Final current price:', sqrtPriceX96ToPrice(finalSqrtPriceX96));
 
    // 多tick区间下跌计算
    const multiTickCalcDown = calculateMultiTickConsumption(
        BigInt(finalSqrtPriceX96),
        finalCurrentTick, 
        BigInt(liquidity),
        tickSpacingInt,
        tickLUT,
        startTick,
        false // 计算token1消耗（价格下跌时实际消耗的token）
    );



    // 多tick区间上涨计算
    const multiTickCalcUp = calculateMultiTickConsumption(
        BigInt(finalSqrtPriceX96),
        finalCurrentTick, 
        BigInt(liquidity),
        tickSpacingInt,
        tickLUT,
        endTick,
        true // 计算token0消耗（价格上涨时实际消耗的token）
    );

  

    // 合并并排序所有分段
    const allSegments = [...multiTickCalcDown.segments, ...multiTickCalcUp.segments];
    
    // 按照平均价格排序
    const liquidssegments = allSegments
        .map(segment => ({
            ...segment,
            avgPrice: (segment.startPrice + segment.endPrice) / 2
        }))
        .sort((a, b) => a.avgPrice - b.avgPrice)
        .map(({ avgPrice, ...segment }) => segment); // 移除临时的avgPrice字段

    return {
        token0cost: multiTickCalcUp.totalTokenConsumption, // 驱动池子上涨离开区间所需的token0
        token1cost: multiTickCalcDown.totalTokenConsumption, // 驱动池子下跌离开区间所需的token1
        liquidssegments: liquidssegments
    };
}


/**
 * 计算单个tick的sqrtx96价格上限和下限
 * 当价格跨过这个tick的上边界和下边界进入next和prev tick的一瞬间，
 * 当前流动池的sqrtx96价格
 * 
 * @param {number} tick - 目标tick
 * @param {number} tickSpacing - tick间距 (如1, 10, 60, 200等)
 * @returns {Object} 包含价格上限、下限和相关信息
 */
function calculateTickPriceBounds(tick, tickSpacing = 1) {
    // 确保tick是有效的初始化tick (必须是tickSpacing的倍数)
    const initializedTick = Math.floor(tick / tickSpacing) * tickSpacing;
    
    // 计算tick的下边界价格 (tick的起始价格)
    const lowerBoundSqrtPriceX96 = tickToSqrtPriceX96(initializedTick);
    
    // 计算tick的上边界价格 (下一个tick的起始价格)
    const upperBoundSqrtPriceX96 = tickToSqrtPriceX96(initializedTick + tickSpacing);
    
    // 转换为普通价格便于查看
    const lowerBoundPrice = sqrtPriceX96ToPrice(lowerBoundSqrtPriceX96);
    const upperBoundPrice = sqrtPriceX96ToPrice(upperBoundSqrtPriceX96);
    
    // 计算价格区间范围
    const priceRange = upperBoundPrice - lowerBoundPrice;
    const priceRangePercentage = (priceRange / lowerBoundPrice) * 100;
    
    return {
        tick: initializedTick,
        tickSpacing: tickSpacing,
        bounds: {
            // 下边界：价格跌穿进入prev tick的临界点
            lower: {
                sqrtPriceX96: lowerBoundSqrtPriceX96.toString(),
                price: lowerBoundPrice,
                description: "价格跌穿此点进入prev tick"
            },
            // 上边界：价格突破进入next tick的临界点  
            upper: {
                sqrtPriceX96: upperBoundSqrtPriceX96.toString(),
                price: upperBoundPrice,
                description: "价格突破此点进入next tick"
            }
        },
        analysis: {
            priceRange: priceRange,
            priceRangePercentage: priceRangePercentage,
            nextTick: initializedTick + tickSpacing,
            prevTick: initializedTick - tickSpacing
        }
    };
}


// 导出工具函数供外部使用
export {
    fetchV3PoolData
};
